<template>
    <v-container>
        <loading-spinner v-if="loading" />
        <div v-else-if="blockRef">
            <back-button fixed />
            <div class="white-card">
                <v-row align="center">
                    <v-col cols="12" md="6">
                        <v-row>
                            <v-col cols="12">
                                <kimsa-text-field :label="$t('common.name')" outlined dense force-value no-elevation
                                    :value="name" @change="name = $event" />
                            </v-col>
                            <v-col cols="12">
                                <kimsa-combobox :label="$t('common.folder')" outlined dense :items="folderOptions"
                                    @change="folder = $event" :value="folder" ref="folders" />
                            </v-col>
                            <v-col cols="12">
                                <kimsa-tags-picker ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                                    :label="$t('exercises.tags')" force-value />
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="12" md="6">
                        <kimsa-upload-file :label="$t('common.image')" relative-path="images/blocks"
                            mime-types="image/*" @change="file = $event" :preview-img="previewImg" ref="img"
                            height="150" />
                    </v-col>

                    <v-col cols="12">
                        <div class="block-title">
                            <div class="block-title-text">
                                {{ $tc('common.blocks', 1) }}
                            </div>
                            <div class="float-right block-exercises-quantity">
                                <v-icon size="20" color="#000000">mdi-dumbbell</v-icon>
                                {{ block.exercises.length }}
                            </div>
                        </div>
                    </v-col>
                    <v-col cols="12" v-if="block.exercises.length === 0">
                        {{ $t('exercises.no_exercises_set') }}
                    </v-col>
                    <v-col cols="12">
                        <draggable v-model="block.exercises" draggable=".draggable-item" handle=".handle-item"
                            ghost-class="ghost">
                            <div v-for="(exercise, i) in block.exercises" :key="exercise.uid"
                                class="draggable-item kimsa-elevation block-exercises">

                                <block-exercise-card :exercise="exercise"
                                    @click:duplicate="duplicateExercise($event, i)"
                                    @click:change="changeExercise($event, i)" @click:delete="deleteExercise(i)"
                                    @click:rest="setRest(i)" @change:config="onConfigChange($event, i)" />

                                <rest-card v-if="exercise.hasTime" :rest="exercise" @click="setRest(i)"
                                    @click:delete="deleteRest(i)" />

                            </div>
                        </draggable>
                        <v-btn color="black" small fab dark depressed @click="showAddExercisesDialog"
                            class="float-right">
                            <v-icon>mdi-dumbbell</v-icon>
                            <v-icon size="12" style="position: absolute; top: -8px; right: 0;">mdi-plus</v-icon>
                        </v-btn>
                    </v-col>
                    <v-col cols="12 text-right">
                        <v-btn color="primary" class="normal-btn black--text" small depressed @click="saveBlock"
                            :loading="saving" :disabled="disabled">
                            {{ $t('common.save') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </div>

            <rest-dialog ref="setRestDialog" @save="onRestSave" />

            <add-exercises-dialog ref="addExerciseDialog" :title="$tc('common.blocks', 1)" :one-exercise="oneExercise"
                :default-filter="defaultFilter" @change="addExercises" />
        </div>
    </v-container>
</template>

<script>

import RestDialog from "@/components/coach/routines/RestDialog";
import RestCard from "@/components/coach/routines/RestCard";
import BlockExerciseCard from "@/components/coach/routines/BlockExerciseCard";
import { randomString } from "@/utils/Common";
import AddExercisesDialog from '@/components/coach/routines/AddExercisesDialog'
import { db } from '@/utils/Firebase'
import { getMergedBlocks } from "@/utils/Exercises";
import BackButton from "@/components/common/BackButton";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import draggable from 'vuedraggable'
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaUploadFile from "@/components/common/inputs/KimsaUploadFile";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { mapActions, mapState } from "vuex";
import { getPayloadBlock } from "@/utils/Exercises";
import KimsaTagsPicker from '../../components/common/inputs/KimsaTagsPicker.vue';

export default {
    components: {
        LoadingSpinner,
        BackButton,
        BlockExerciseCard,
        RestCard,
        RestDialog,
        AddExercisesDialog,
        draggable,
        KimsaCombobox,
        KimsaUploadFile,
        KimsaTextField,
        KimsaTagsPicker
    },
    data() {
        return {
            loading: false,
            name: '',
            folder: null,
            file: null,
            block: null,
            blockRef: null,
            oneExercise: false,
            exerciseIndex: null,
            activeExerciseIndex: null,
            defaultFilter: null,
            saving: false,
            tags: []
        }
    },
    computed: {
        ...mapState(['folders', 'globalFolders']),
        blockId(vm = this) {
            return vm.$route.params.uid
        },
        disabled(vm = this) {
            return !vm.name || !vm.folder || vm.block.exercises.length === 0 || vm.block.exercises.some(ex => !ex.config || ex?.config?.sets?.length === 0)
        },
        folderOptions(vm = this) {
            return [...vm.globalFolders, ...vm.folders].filter(folder => folder.type === 'blocks').map(folder => ({ id: folder.id, name: folder.name })) || []
        },
        previewImg(vm = this) {
            return vm.blockRef?.img || vm.file?.img || null
        },
        coachId(vm = this) {
            return vm.currentUser.uid
        }
    },
    async mounted() {
        const vm = this
        if (vm.folders.length === 0) await vm.bindFolders(vm.coachId)
        if (vm.globalFolders.length === 0) await vm.bindGlobalFolders()
        await vm.loadBlock()
    },
    methods: {
        ...mapActions(['bindFolders', 'bindGlobalFolders']),
        async loadBlock() {
            const vm = this
            vm.loading = true
            const blockRef = await db.collection('blocks').doc(vm.blockId).get()
            vm.blockRef = blockRef.data()
            vm.name = vm.blockRef.name
            vm.folder = vm.folderOptions.find(folder => folder.id === vm.blockRef.folder)
            let merged = await getMergedBlocks([vm.blockRef.block])
            vm.block = merged[0]
            vm.tags = vm.blockRef?.tags || []
            vm.loading = false
        },
        showAddExercisesDialog() {
            const vm = this
            vm.oneExercise = false
            vm.$refs.addExerciseDialog.show()
        },
        onRestSave(time) {
            const vm = this
            vm.block.exercises[vm.activeExerciseIndex].hasTime = true
            vm.block.exercises[vm.activeExerciseIndex].time = time
        },
        addExercises(exercises) {
            const vm = this
            if (vm.oneExercise) return vm.block.exercises.splice(vm.exerciseIndex, 1, exercises[0])
            let newExercises = exercises.map(ex => {
                ex.uid = randomString()
                return ex
            })
            vm.block.exercises = [...vm.block.exercises, ...newExercises]
        },
        duplicateExercise(exercise, exerciseIndex) {
            const vm = this
            vm.block.exercises.splice(exerciseIndex, 0, exercise)
        },
        changeExercise(exercise, exerciseIndex) {
            const vm = this
            vm.exerciseIndex = exerciseIndex
            vm.oneExercise = true
            vm.defaultFilter = { categories: exercise.categories[0], types: exercise.types }
            vm.$refs.addExerciseDialog.show()
        },
        deleteExercise(exerciseIndex) {
            const vm = this
            vm.block.exercises.splice(exerciseIndex, 1)
        },
        setRest(exerciseIndex = null) {
            const vm = this
            if (exerciseIndex >= 0) {
                vm.activeExerciseIndex = exerciseIndex
                let time = vm.block.exercises[exerciseIndex]?.time || '00:00:00'
                vm.$refs.setRestDialog.showEdit(time)
            } else {
                vm.$refs.setRestDialog.show()
            }
        },
        onConfigChange(config, exerciseIndex) {
            const vm = this
            let template = { ...vm.block.exercises[exerciseIndex], config: config }
            vm.block.exercises.splice(exerciseIndex, 1, template)
        },
        deleteRest(exerciseIndex) {
            const vm = this
            vm.block.exercises[exerciseIndex].hasTime = false
            vm.block.exercises[exerciseIndex].time = ''
        },
        async saveBlock() {
            const vm = this;
            vm.saving = true
            let img = { img: vm.blockRef.img, imgSavePath: vm.blockRef.imgSavePath }
            if (vm.file) img = await vm.$refs.img.upload()
            let payload = getPayloadBlock(vm.name, vm.coachId, vm.block, vm.folder, vm.tags, img)
            await db.collection('blocks').doc(vm.blockId).update(payload)
            vm.saving = false
            vm.toast(vm.$t('common.saved_block'))
            vm.$router.back()
        },
    }

}

</script>

<style scoped>
.block-title {
    background-color: #CCFF99;
    padding: 12px;
    border: 1px solid rgba(51, 51, 51, 0.1);
}

.block-title-text {
    font-size: 20px;
    color: #333333;
    text-transform: uppercase;
    font-weight: bold;
    display: inline;
}

.block-exercises-quantity {
    font-size: 20px;
    font-weight: bold;
}

.block-exercises {
    margin-bottom: 12px;
    border-radius: 8px;
}

.ghost {
    width: 100%;
    overflow: hidden;
    background-color: #ccc;
}
</style>